import { useCallback, useEffect, useState } from 'react';
import { getLocalDateString } from '@shared/utils/dateUtils';
import { usePaywall } from './usePaywall';
import { useAuth } from '../context/AuthContext';
import { useDayChange } from './useDayChange';

/**
 * Free-tier chat gate: FREE_DAILY_CHAT_LIMIT messages per local day, then the
 * paywall. Members (data_sync / early_access) bypass it entirely — their only
 * cap is the server-side abuse limit. Counted client-side in localStorage; the
 * server limit remains the backstop.
 */

export const FREE_DAILY_CHAT_LIMIT = 3;

interface StoredUsage {
  date: string;
  count: number;
}

const storageKey = (uid: string) => `enough_chat_free_${uid}`;

const readUsage = (uid: string): StoredUsage => {
  const today = getLocalDateString();
  try {
    const raw = localStorage.getItem(storageKey(uid));
    if (raw) {
      const parsed = JSON.parse(raw) as StoredUsage;
      if (parsed.date === today && typeof parsed.count === 'number') return parsed;
    }
  } catch {
    // Corrupt/unavailable storage — treat as a fresh day
  }
  return { date: today, count: 0 };
};

interface ChatFreeTierGate {
  /**
   * Call before sending a message. Returns true to proceed. For free users at
   * the limit, shows the paywall and returns false (the "11th chat" moment).
   */
  tryConsumeMessage: () => boolean;
  /** Give back a consumed message when the send failed (nothing was delivered). */
  refundMessage: () => void;
  /** Free messages left today; undefined for members (unlimited). */
  freeMessagesRemaining?: number;
}

export function useChatFreeTierGate(): ChatFreeTierGate {
  const { currentUser } = useAuth();
  // Chat member limits are deliberately ANY-paid (isDataConnect), not
  // spending-only: the chat backend grants member limits to every paid
  // level, and the client must match the server.
  const { isDataConnect, isReady, showPaywall } = usePaywall();
  const localDate = useDayChange();
  const [usedToday, setUsedToday] = useState(() =>
    currentUser ? readUsage(currentUser.uid).count : 0
  );

  // Re-read the count when the user or the local day changes — otherwise the
  // "N free messages left" banner shows yesterday's (or another account's)
  // count until the next send.
  useEffect(() => {
    setUsedToday(currentUser ? readUsage(currentUser.uid).count : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, localDate]);

  // Fail open while subscription state is loading or signed out — never block
  // a paying member on a slow entitlement check.
  const gated = isReady && !isDataConnect && !!currentUser;

  const tryConsumeMessage = useCallback((): boolean => {
    if (!gated || !currentUser) return true;

    const usage = readUsage(currentUser.uid);
    if (usage.count >= FREE_DAILY_CHAT_LIMIT) {
      showPaywall({ source: 'chat_free_tier' });
      return false;
    }

    const next: StoredUsage = { date: usage.date, count: usage.count + 1 };
    try {
      localStorage.setItem(storageKey(currentUser.uid), JSON.stringify(next));
    } catch {
      // Storage full/unavailable — still allow the message
    }
    setUsedToday(next.count);
    return true;
  }, [gated, currentUser, showPaywall]);

  const refundMessage = useCallback((): void => {
    if (!gated || !currentUser) return;
    const usage = readUsage(currentUser.uid);
    const next: StoredUsage = { date: usage.date, count: Math.max(0, usage.count - 1) };
    try {
      localStorage.setItem(storageKey(currentUser.uid), JSON.stringify(next));
    } catch {
      // Storage unavailable — nothing to refund
    }
    setUsedToday(next.count);
  }, [gated, currentUser]);

  return {
    tryConsumeMessage,
    refundMessage,
    freeMessagesRemaining: gated
      ? Math.max(0, FREE_DAILY_CHAT_LIMIT - usedToday)
      : undefined,
  };
}
